import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import LayoutComponent from './LayoutComponent';
import ThemeToggleButton from './ThemeToggleButton';

const getAvatarInitials = (fullName = '') => {
  const words = String(fullName)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
};

const Header = ({
  logo = "Soquimat ERP",
  navigationItems = [],
  activeItem = null,
  searchPlaceholder = "Search...",
  searchShortcut = "⌘K",
  user = null,
  onLogout = () => {},
  onRefresh = null,
  onThemeChange,
  colorMode = 'light',
  isDarkMode = false,
  className = "",
  ...props
}) => {
  const [searchValue, setSearchValue] = useState('');
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const userMenuRef = useRef(null);
  const refreshTimeoutRef = useRef(null);
  const navigate = useNavigate();
  const resolvedMode = colorMode || (isDarkMode ? 'dark' : 'light');
  const currentTheme = useTheme(resolvedMode);

  useEffect(() => {
    if (!userMenuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setUserMenuOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [userMenuOpen]);

  useEffect(() => () => {
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
  }, []);

  const headerCustomStyles = {
    ...props.style
  };

  const logoStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: currentTheme.spacing.sm,
    fontSize: currentTheme.typography.fontSize.lg,
    fontWeight: currentTheme.typography.fontWeight.bold,
    color: currentTheme.colors.text.primary,
    textDecoration: 'none',
    marginRight: currentTheme.spacing.xl,
    cursor: 'pointer',
    transition: 'color 0.3s ease-in-out'
  };

  const logoIconStyles = {
    width: '35px',
    height: '35px',
    flexShrink: 0
  };

  const navStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: currentTheme.spacing.lg,
    flex: 1
  };

  const navItemStyles = {
    fontSize: currentTheme.typography.fontSize.sm,
    color: currentTheme.colors.text.secondary,
    textDecoration: 'none',
    padding: `${currentTheme.spacing.sm} 0`,
    position: 'relative',
    cursor: 'pointer',
    transition: 'color 0.2s ease-in-out'
  };

  const activeNavItemStyles = {
    ...navItemStyles,
    color: currentTheme.colors.interactive.primary,
    fontWeight: currentTheme.typography.fontWeight.semibold,
    backgroundColor: resolvedMode === 'dark'
      ? 'rgba(0, 122, 204, 0.12)'
      : resolvedMode === 'regular'
        ? 'rgba(26, 171, 184, 0.12)'
        : 'rgba(0, 102, 204, 0.08)',
    borderRadius: currentTheme.borderRadius.sm,
    padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`
  };

  const activeIndicatorStyles = {
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '20px',
    height: '3px',
    backgroundColor: currentTheme.colors.interactive.primary,
    borderRadius: '2px',
    boxShadow: `0 0 8px ${currentTheme.colors.interactive.primary}40`
  };

  const searchContainerStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 'auto',
    marginRight: currentTheme.spacing.lg
  };

  const searchInputStyles = {
    padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`,
    paddingRight: '80px',
    backgroundColor: currentTheme.colors.secondary,
    border: `1px solid ${currentTheme.colors.border.primary}`,
    borderRadius: currentTheme.borderRadius.md,
    fontSize: currentTheme.typography.fontSize.sm,
    color: currentTheme.colors.text.primary,
    outline: 'none',
    width: '280px',
    transition: 'all 0.2s ease-in-out',
    fontFamily: currentTheme.typography.fontFamily.primary
  };

  const searchIconStyles = {
    position: 'absolute',
    right: '40px',
    color: currentTheme.colors.text.muted,
    fontSize: '14px',
    pointerEvents: 'none'
  };

  const searchShortcutStyles = {
    position: 'absolute',
    right: '8px',
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: currentTheme.typography.fontSize.xs,
    color: currentTheme.colors.text.muted,
    backgroundColor: currentTheme.colors.tertiary,
    padding: '2px 6px',
    borderRadius: currentTheme.borderRadius.sm,
    fontFamily: currentTheme.typography.fontFamily.mono
  };

  const refreshButtonStyles = {
    marginLeft: currentTheme.spacing.md,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 36,
    height: 36,
    borderRadius: currentTheme.borderRadius.md,
    border: `1px solid ${currentTheme.colors.border.primary}`,
    backgroundColor: currentTheme.colors.secondary,
    color: currentTheme.colors.text.secondary,
    cursor: isRefreshing ? 'wait' : 'pointer',
    padding: 0,
    transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  };

  const themeToggleContainerStyles = {
    marginLeft: currentTheme.spacing.md,
    display: 'flex',
    alignItems: 'center'
  };

  const userMenuContainerStyles = {
    position: 'relative',
    marginLeft: currentTheme.spacing.md,
    display: 'flex',
    alignItems: 'center'
  };

  const avatarButtonStyles = {
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: `1px solid ${currentTheme.colors.border.primary}`,
    backgroundColor: currentTheme.colors.interactive.primary,
    color: currentTheme.colors.text.accent,
    fontSize: 12,
    fontWeight: currentTheme.typography.fontWeight.bold,
    fontFamily: currentTheme.typography.fontFamily.primary,
    letterSpacing: '0.02em',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    transition: 'opacity 0.15s ease',
    boxShadow: userMenuOpen ? `0 0 0 2px ${currentTheme.colors.interactive.primary}40` : 'none'
  };

  const dropdownStyles = {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    right: 0,
    minWidth: 168,
    backgroundColor: currentTheme.colors.primary,
    border: `1px solid ${currentTheme.colors.border.primary}`,
    borderRadius: currentTheme.borderRadius.md,
    boxShadow: currentTheme.shadows?.md || '0 8px 24px rgba(0,0,0,0.12)',
    padding: 4,
    zIndex: 1000
  };

  const menuItemStyles = {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    border: 'none',
    background: 'transparent',
    color: currentTheme.colors.text.primary,
    fontSize: currentTheme.typography.fontSize.sm,
    fontFamily: currentTheme.typography.fontFamily.primary,
    padding: `${currentTheme.spacing.sm} ${currentTheme.spacing.md}`,
    borderRadius: currentTheme.borderRadius.sm,
    cursor: 'pointer'
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  const handleSearchFocus = (e) => {
    e.target.style.borderColor = currentTheme.colors.interactive.primary;
    e.target.style.backgroundColor = currentTheme.colors.hover;
  };

  const handleSearchBlur = (e) => {
    e.target.style.borderColor = currentTheme.colors.border.primary;
    e.target.style.backgroundColor = currentTheme.colors.secondary;
  };

  const handleNavItemHover = () => {};
  const handleNavItemLeave = () => {};

  const handleProfileClick = () => {
    setUserMenuOpen(false);
    navigate('/perfil');
  };

  const handleAgendaClick = () => {
    setUserMenuOpen(false);
    navigate('/agenda');
  };

  const handleLogoutClick = () => {
    setUserMenuOpen(false);
    onLogout();
  };

  const handleRefreshClick = () => {
    if (!onRefresh || isRefreshing) return;
    setIsRefreshing(true);
    onRefresh();
    if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => {
      setIsRefreshing(false);
    }, 700);
  };

  const initials = getAvatarInitials(user?.full_name || '');

  return (
    <LayoutComponent
      componentType="header"
      isDarkMode={resolvedMode === 'dark'}
      className={className}
      customStyles={headerCustomStyles}
    >
      {/* Logo */}
      <Link to="/" style={logoStyles}>
        <img
          src={resolvedMode === 'dark' ? "/assets/soquimat-dark.png" : "/assets/soquimat-light.png"}
          alt="Soquimat ERP"
          style={logoIconStyles}
        />
        {logo}
      </Link>

      {/* Navigation */}
      <nav style={navStyles}>
        {navigationItems.map((item, index) => (
          <Link
            key={index}
            to={item.href || '#'}
            style={item.active ? activeNavItemStyles : navItemStyles}
            onMouseEnter={handleNavItemHover}
            onMouseLeave={handleNavItemLeave}
            onClick={() => {
              if (item.onClick) {
                item.onClick();
              }
            }}
          >
            {item.text}
            {item.active && <div style={activeIndicatorStyles} />}
          </Link>
        ))}
      </nav>

      {/* Search */}
      <div style={searchContainerStyles}>
        <input
          type="text"
          placeholder={searchPlaceholder}
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          style={searchInputStyles}
        />
        <span style={searchIconStyles}>🔍</span>
        <span style={searchShortcutStyles}>{searchShortcut}</span>
      </div>

      {/* Refresh current view */}
      {onRefresh ? (
        <button
          type="button"
          style={refreshButtonStyles}
          onClick={handleRefreshClick}
          disabled={isRefreshing}
          title="Actualizar vista"
          aria-label="Actualizar tablas, gráficos y cards"
          onMouseEnter={(e) => {
            if (isRefreshing) return;
            e.currentTarget.style.backgroundColor = currentTheme.colors.hover;
            e.currentTarget.style.color = currentTheme.colors.text.primary;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = currentTheme.colors.secondary;
            e.currentTarget.style.color = currentTheme.colors.text.secondary;
          }}
        >
          <RefreshCw
            size={16}
            aria-hidden
            style={{
              animation: isRefreshing ? 'soquimat-header-spin 0.7s linear' : 'none',
            }}
          />
        </button>
      ) : null}

      {/* Theme Toggle */}
      {onThemeChange && (
        <div style={themeToggleContainerStyles}>
          <ThemeToggleButton
            colorMode={resolvedMode}
            onChange={onThemeChange}
          />
        </div>
      )}

      {/* User avatar menu */}
      {user && (
        <div style={userMenuContainerStyles} ref={userMenuRef}>
          <button
            type="button"
            style={avatarButtonStyles}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            aria-label={`Menú de ${user.full_name || 'usuario'}`}
            title={user.full_name || 'Usuario'}
            onClick={() => setUserMenuOpen((open) => !open)}
          >
            {initials}
          </button>

          {userMenuOpen && (
            <div style={dropdownStyles} role="menu">
              <button
                type="button"
                role="menuitem"
                style={menuItemStyles}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentTheme.colors.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={handleProfileClick}
              >
                Mi Perfil
              </button>
              <button
                type="button"
                role="menuitem"
                style={menuItemStyles}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentTheme.colors.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={handleAgendaClick}
              >
                Mi Agenda
              </button>
              <button
                type="button"
                role="menuitem"
                style={menuItemStyles}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = currentTheme.colors.hover;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
                onClick={handleLogoutClick}
              >
                Salir
              </button>
            </div>
          )}
        </div>
      )}
    </LayoutComponent>
  );
};

export default Header;
